import {log} from "@cling/lib.shared.logging"
import * as React from "react"
import {render} from "preact"
import {
    board_info_resource,
    board_resource,
    init as init_resources,
    media_info_resource,
} from "@cling/lib.web.resources"
import {
    add_provisional_thumbnail,
    init as init_thumbnails,
    remove_provisional_thumbnail,
    thumbnail_url,
} from "@cling/lib.web.resources/thumbnails"
import {init as init_block_sync} from "./block_sync_updates_if_pending_patches"
import {init as init_upload} from "../upload/upload"
import {init as init_service_worker} from "./service_worker_gateway"
import {init as init_worker_gateway} from "./worker_gateway"
import {init as init_intercom} from "../misc/intercom"
import {init as init_push_notifications} from "../account/push_notifications"
import {
    enforce_refresh,
    start as start_auth,
    logout,
    on_authentication_error,
    register_auth_listener,
    wait_until_authenticated,
    AuthInfoExtended,
    can_call_faas_promise,
    set_auth_info,
    can_call_faas,
} from "@cling/lib.web.auth"
import {
    Account,
    AccountAnalytics,
    AccountAttributes,
    AccountAuth,
    AccountSettings,
    AccountUID,
    as_TeamUID,
    AuthInfo,
    BlobUID,
    BoardUID,
    BrowserEnv,
    Client,
    MediaInfo,
    is_system_board_uid,
    Locale,
    Login,
    ReportUserEventRequest,
    SessionUID,
    SignUp,
    SyncEntityType,
    UserEvent,
    as_BoardUID,
    board_templates_board_uid,
    OrganizationUID,
    AuthProvider,
    TeamUID,
    FeatureSwitch,
    Plan,
    PatchFullAccountRequest,
    create_PatchUID,
    PatchAccountAuth,
    extract_linked_board_and_card_uid,
    as_BlobUID,
    QueryThumbnailRequest,
    to_b64url,
    ThumbnailImageType,
    PatchUID,
    Patch,
    to_buffer,
    background_images_board_uid,
    title_images_board_uid,
    BlobUIDMapping,
} from "@cling/lib.shared.model"
import {
    assert,
    ControllablePromise,
    full_name,
    new_ControllablePromise,
    nop,
    not_null,
    Set_equal,
    sleep,
    TimeoutError,
} from "@cling/lib.shared.utils"
import {call_function, init as init_faas} from "@cling/lib.shared.faas"
import {init_log_event_reporting} from "./init_logging"
import {is_avif_supported, is_webp_supported, running_on_mobile_device} from "@cling/lib.web.utils"
import {set_fatal_error_url_params} from "@cling/lib.web.utils/fatal_error_url"
import {load_js} from "@cling/lib.web.lazy_load"
import {init as init_cling_button} from "../cling_button_gateway"
import {query_param} from "@cling/lib.web.utils/query_param"
import {init as init_i18n} from "@cling/lib.web.i18n"
import {init as init_download} from "../misc/download"
import * as state from "../state/index"
import {
    init as init_analytics,
    parse_source_campaign,
    report_user_event,
    store_source_campaign_to_local_storage,
} from "@cling/lib.web.analytics"
import {App} from "../app"
import {sync_initial_state} from "./initial_state"
import {autorun, makeObservable, observable, runInAction} from "mobx"
import {
    init as init_sync_with_worker,
    request_sync_factory,
    wait_for_sync_factory,
} from "./sync_with_worker"
import {ClingError} from "@cling/lib.shared.error"
import {EventBus, local_eventbus} from "@cling/lib.web.primitives"
import {report_error, report_info} from "@cling/lib.shared.debug"
import {random_uid} from "@cling/lib.shared.crypto"
import {start_drag_card} from "../drag_and_drop"
import type {
    AddProvisionalMediaInfo,
    PatchPublished,
    Ping,
    PublishPatch,
    RequestSync,
    RequestSyncAllFromStore,
    Search,
    SendSearchResult,
} from "@cling/lib.web.worker/worker_interface"
import type {UID} from "@cling/lib.shared.types/common"
import type {LocalCache} from "@cling/lib.web.resources/local_cache"
import type {SearchQuery} from "@cling/lib.shared.search"
import type {CallSearchResult} from "../state/search_state"
import {
    init_dev,
    misc_init,
    preload_assets,
    set_global_css_classes,
    set_user_currency,
} from "./startup_common"
import {goto_board} from "../utils"
import {safe_session_storage} from "@cling/lib.web.utils/safe_storage"
import {board_name} from "../board/board_name"
import p_limit from "p-limit"
import {set_board_size} from "../state/patch"
import {navigate} from "@reach/router/index"
import {supported_thumbnail_dims} from "@cling/lib.shared.faas/constants"

const report_user_event_queue = p_limit(1)
const analytics_initialized = new_ControllablePromise<void>()

export async function start_web_app(
    session_uid: SessionUID,
    set_state: (value: "starting" | "waiting for authentication") => void,
): Promise<boolean> {
    parse_source_campaign()
    set_fatal_error_url_params({s: session_uid})
    const {frontend_only, dev} = init_dev()
    misc_init({dev})
    set_global_css_classes()
    const silent_login = location.search.includes("silent_login")
    const special_mode = frontend_only ? ", frontend_only" : silent_login ? ", silent_login" : ""
    log.debug(`start_web_app()${special_mode} ${cling.version.substring(0, 7)} ${session_uid}`)
    const client = running_on_mobile_device() ? Client.web_app_mobile : Client.web_app_desktop
    let auth_info: AuthInfoExtended
    let csrf_token: string
    if (frontend_only) {
        auth_info = {
            account_uid: "a_________fake_paul" as AccountUID,
            team_uids: [
                as_TeamUID("t_________________boys"),
                as_TeamUID("t___________olsenbande"),
                as_TeamUID("t____english_for_runaways"),
            ],
            admin_organization_uids: [],
            auth_providers: [AuthProvider.username_password],
        }
        set_auth_info(auth_info)
    } else {
        const initial_auth_info = await start_auth({
            remember_url: !silent_login,
            update_csrf_token: (token: string) => (csrf_token = token),
        })
        if (initial_auth_info) {
            auth_info = initial_auth_info
        } else {
            log.info("start_web_app() - waiting for authentication ...")
            set_state("waiting for authentication")
            auth_info = await wait_until_authenticated()
        }
        set_state("starting")
    }
    assert(auth_info, "`auth_info` should be set by now")
    // Notify code running outside of this app (index.template.ts) that
    // we are logged in.
    cling.authenticated = true
    set_fatal_error_url_params({a: auth_info.account_uid, s: session_uid})
    init_cling_button(silent_login)
    if (silent_login) {
        log.info("start_web_app() - this is a silent login ...")
        return false
    }
    const await_before_render: Promise<void>[] = []
    preload_assets({await_before_render})
    let auth_state: (AuthInfo & {access_token: string}) | undefined
    register_auth_listener((auth_state_) => {
        auth_state = auth_state_
        auth_info = {
            account_uid: auth_state.account_uid,
            team_uids: auth_state.team_uids,
            admin_organization_uids: auth_state.admin_organization_uids,
            auth_providers: auth_state_.auth_providers,
        }
        // Some FAAS like `query-thumbnail` require the faas-trace to be set in a cookie
        // because they are called from a <img> tag.
        const faas_trace = `|web_app:${process.env.VERSION}:${
            auth_state?.account_uid || auth_info?.account_uid
        }:${session_uid}|`
        // We need to mark the cookie as coming from "dev" because when logged in
        // into the production and a dev environment at the same time, both cookies
        // are sent to the dev-server.
        document.cookie = `${dev ? "devf" : "f"}=${faas_trace}; domain=.${
            location.hostname
        } ;path=/; SameSite=Strict; Secure`
        // TODO: This is only used in tests - refactor the test and remove this code!
        ;(cling as any).auth_state = auth_state
    })
    init_faas({
        /**
         * Block until we are authenticated.
         */
        request_modifier: async (req) => {
            const t0 = Date.now()
            // Busy wait until we are authenticated.
            while (!auth_state) {
                // `manage-session` is called before `auth_state` is set. We have to let it through
                // to avoid a deadlock.
                if (req.url?.endsWith("/f/manage-session")) {
                    return
                }
                if (Date.now() - t0 > 30_000) {
                    throw new ClingError("Timeout waiting for authentication", 503)
                }
                await sleep(100)
            }
            req.headers["x-cling-csrf-token"] = csrf_token
        },
        on_401: on_authentication_error,
    })
    init_log_event_reporting(client)
    const is_new_user = _detect_and_report_login_or_signup()
    const thumbnail_image_type = (await is_avif_supported())
        ? ThumbnailImageType.avif
        : (await is_webp_supported())
          ? ThumbnailImageType.webp
          : ThumbnailImageType.png
    log.debug("start_web_app() - starting / connecting worker ...")
    const port = await start_worker({
        account_uid: auth_info.account_uid,
        ping_message: {type: "ping", thumbnail_image_type, location_search: location.search},
    })
    log.debug("start_web_app() - wiring up caches, stores and event-buses ...")
    const publish_patch_bus = local_eventbus<PublishPatch | PatchPublished>()
    const {ignore_sync_update, publish_patch: _publish_patch} = init_block_sync(publish_patch_bus)
    const {caches, local_cache_sync_event_bus, pause_board_sync} = await init_sync_with_worker(
        (msg) => {
            if (msg.sync_entity_type === SyncEntityType.board && msg.type === "sync_put") {
                set_board_size(as_BoardUID(msg.uid), msg.full_pb.length)
            }
            return ignore_sync_update(msg)
        },
    )
    const request_sync_bus = local_eventbus<RequestSync | RequestSyncAllFromStore>()
    const request_sync = (uid: UID, type: SyncEntityType) =>
        request_sync_bus.postMessage({
            type: "request_sync",
            uid,
            sync_entity_type: type,
        })
    const search_bus = local_eventbus<Search | SendSearchResult>()
    const add_provisional_media_info_bus = local_eventbus<AddProvisionalMediaInfo>()
    init_worker_gateway({
        auth_info_provider: () => not_null(auth_info),
        csrf_token_provider: () => csrf_token,
        port,
        sync_updates_bus: local_cache_sync_event_bus,
        request_sync_bus,
        publish_patch_bus,
        add_provisional_media_info_bus,
        search_bus,
        ping_message: {type: "ping", thumbnail_image_type, location_search: location.search},
    })
    log.debug("start_web_app() - eagerly requesting BoardInfo, Team and Account entities ...")
    eagerly_request_sync(request_sync_bus)
    log.debug("start_web_app() - fetching initial state ...")
    const current_user = await sync_initial_state({
        account_uid: auth_info.account_uid,
        caches,
        request_sync,
        board_uid_hint: board_uid_hint(location.href),
    })
    current_user.is_new = is_new_user
    if (cling.frontend_only) {
        // We have to do this again, because only now everything is available.
        eagerly_request_sync(request_sync_bus)
    }
    log.debug("start_web_app() - initializing resources ...")
    init_resources({
        current_user,
        caches,
        request_sync: request_sync_factory({request_sync}),
        wait_for_sync: wait_for_sync_factory({request_sync}),
    })
    init_upload({
        add_provisional_thumbnail: async ({
            blob_uid,
            mime_type,
            image_file,
            image_height,
            image_width,
        }: {
            blob_uid: BlobUID
            mime_type: string
            image_file: File
            image_width: number
            image_height: number
        }) => {
            try {
                const media_info = new MediaInfo({
                    width: image_width,
                    height: image_height,
                    blob_uid,
                    version: 0,
                    mime_type,
                    size: image_file.size,
                })
                add_provisional_media_info_bus.postMessage({
                    type: "add_provisional_media_info",
                    media_info: media_info,
                })
                await add_provisional_thumbnail({
                    blob_uid,
                    mime_type,
                    file: image_file,
                    width: image_width,
                    height: image_height,
                })
                // Request the correct MediaInfo once the server has generated it.
                // This will also trigger the removal of the provisional thumbnail (see below).
                request_sync(blob_uid, SyncEntityType.media_info)
            } catch (error) {
                report_error("Failed to add provisional thumbnail", error)
            }
        },
    })
    // Remove provisional thumbnails once they are no longer needed.
    local_cache_sync_event_bus.addEventListener((msg) => {
        if (
            msg.data.sync_entity_type === SyncEntityType.media_info &&
            (msg.data.type === "sync_put" || msg.data.type === "sync_up_to_date")
        ) {
            if (msg.data.version === 0) {
                // This is the provisional MediaInfo, we only remove the provisional thumbnail
                // once the server has generated the correct MediaInfo.
                return
            }
            remove_provisional_thumbnail(as_BlobUID(msg.data.uid))
                .then((removed) => {
                    if (removed) {
                        // Make sure everything renders again.
                        ui_state.inc_image_cache_thrash_index(as_BlobUID(msg.data.uid))
                    }
                })
                .catch(report_error)
        }
    })
    const frontend_only_blob_uid_mapping: BlobUIDMapping[] = []
    const copy_media_promises: Promise<void>[] = []
    const publish_patch = (args: {
        patch_uid: PatchUID
        board_uids: BoardUID[]
        patch: Patch
        transformed_patches: Patch[]
        patch_pb: Uint8Array
        transformed_patches_pb: Uint8Array[]
    }) => {
        _publish_patch(args)
        const blob_uid_mapping = [...args.patch.blob_uid_mapping]
        for (const p of args.transformed_patches) {
            blob_uid_mapping.push(...p.blob_uid_mapping)
        }
        for (const {src} of blob_uid_mapping) {
            media_info_resource.read(src)
        }
        if (frontend_only) {
            frontend_only_blob_uid_mapping.push(...blob_uid_mapping)
        }
        ;(async () => {
            // Wait until all previous copies have finished.
            // This is necessary because a copy might be copied again (e.g. when a card is copied
            // to the clipboard and then pasted).
            while (copy_media_promises.length > 0) {
                await copy_media_promises.shift()
            }
            for (const {src, target} of blob_uid_mapping) {
                let media_info = media_info_resource.read(src)
                const t0 = Date.now()
                while (Date.now() - t0 < 500 && !media_info) {
                    await sleep(50)
                    media_info = media_info_resource.read(src)
                }
                if (!media_info || media_info?.invalid) {
                    continue
                }
                const new_media_info = new MediaInfo({...media_info, blob_uid: target})
                add_provisional_media_info_bus.postMessage({
                    type: "add_provisional_media_info",
                    media_info: new_media_info,
                })
                caches[SyncEntityType.media_info].put(target, to_buffer(new_media_info))
                // Request the correct MediaInfo once the server has generated it.
                // This will also trigger the removal of the provisional thumbnail (see above).
                request_sync(new_media_info.blob_uid, SyncEntityType.media_info)
                copy_media_promises.push(
                    (async () => {
                        // Request the largest size because the SW will send us what's cached anyway.
                        const url = thumbnail_url({
                            blob_uid: src,
                            width: supported_thumbnail_dims[supported_thumbnail_dims.length - 1],
                            height: supported_thumbnail_dims[supported_thumbnail_dims.length - 1],
                        })
                        const res = await fetch(url)
                        await add_provisional_thumbnail({
                            blob_uid: new_media_info.blob_uid,
                            mime_type: new_media_info.mime_type,
                            file: (await res.blob()) as File,
                            width: new_media_info.width,
                            height: new_media_info.height,
                        })
                    })().catch(report_info),
                )
            }
            while (copy_media_promises.length > 0) {
                await copy_media_promises.shift()
            }
        })().catch(report_error)
    }
    const user_locale = query_param("__locale", () => current_user.account.locale) as Locale
    current_user.account.locale = user_locale
    await init_i18n(user_locale)
    init_download(() => false, can_call_faas)
    const ui_state = state.init({
        current_user,
        call_search: call_search_factory(search_bus),
        publish_patch,
        pause_board_sync,
    })
    assert(state.current_user, "`state.current_user` has not been set yet.")
    _sync_current_user(caches)
    _sync_claims()
    _sync_auth_providers_with_server()
    init_service_worker().catch(report_error)
    log.debug("start_web_app() - initializing thumbnails ...")
    if (frontend_only) {
        const thumbnails = {
            m_empire_state_building: "empire_state_building.jpg",
            m_empire_state_building2: "empire_state_building.jpg",
            m_empire_state_building3: "empire_state_building.jpg",
            m_empire_state_building4: "empire_state_building.jpg",
            m_ferrari_formula1_2004_jpeg: "schumacher_ferrari.jpg",
            m_ferrari_formula1_2004_jpeg2: "schumacher_ferrari.jpg",
            m_ferrari_formula1_2004_jpeg3: "schumacher_ferrari.jpg",
            m_________avatar2_png: "avatar2.png",
            m_________avatar2_png2: "avatar2.png",
            m_________avatar2_png3: "avatar2.png",
            m______________empty_png: "empty.png",
        } as {[blob_uid: BlobUID]: string}
        await init_thumbnails({
            thumbnail_url: ({blob_uid}) => {
                const resolved_blob_uid =
                    frontend_only_blob_uid_mapping.find((x) => x.target === blob_uid)?.src ||
                    blob_uid
                const name = thumbnails[resolved_blob_uid]
                return `https://storage.googleapis.com/lib_shared_test_assets/${name}`
            },
            image_cache_thrash_index: () => 0,
        })
    } else {
        await init_thumbnails({
            thumbnail_url: (args) => {
                const req = new QueryThumbnailRequest({...args, image_type: thumbnail_image_type})
                return `https://${location.hostname}/query-thumbnail?q=${to_b64url(req)}`
            },
            image_cache_thrash_index: state.ui_state.image_cache_thrash_index.bind(state.ui_state),
        })
    }
    init_analytics({
        client,
        client_version: cling.version,
        current_user_info: () => ({
            tracking_id: current_user.account_attributes.tracking_id,
            locale: current_user.account.locale,
            feature_switches: current_user.account_attributes.feature_switches,
            plan: current_user.account_attributes.plan,
            team_uids: current_user.account_attributes.team_uids,
            cohort_date: current_user.account.first_change.date,
        }),
        send_user_event: frontend_only
            ? nop
            : (user_event: UserEvent, browser_env: BrowserEnv) => {
                  const board_info = user_event.board_uid
                      ? board_info_resource.read(user_event.board_uid)
                      : undefined
                  browser_env.document_referrer = document.referrer.startsWith(
                      `https://${location.host}`,
                  )
                      ? ""
                      : document.referrer
                  if (board_info?.indexed_by_search_engines) {
                      // Use the canonical board name.
                      browser_env.document_title = `[pb] ${board_name(board_info)}`
                  } else {
                      // Anonymize browser_env for private boards.
                      browser_env.document_title = "__private_board"
                      browser_env.document_location = `${location.protocol}//${location.host}/c/__private_board`
                  }
                  report_user_event_queue(() =>
                      can_call_faas_promise()
                          .then(() =>
                              call_function(
                                  new ReportUserEventRequest({
                                      user_events: [user_event],
                                      browser_env,
                                  }),
                              ).catch(report_error),
                          )
                          .catch(report_error),
                  ).catch(report_error)
              },
        current_board_uid: () => ui_state.current_board_uid,
    })
    analytics_initialized.resolve()
    init_intercom({
        crm_id: current_user.account_attributes.crm_id,
        email: current_user.account_attributes.email,
        full_name: full_name(current_user.account),
    })
    init_push_notifications()
    log.debug("start_web_app() - waiting for `await_before_render` ...")
    await Promise.all(await_before_render)
    if (current_user.account_attributes?.create_organization_uid) {
        navigate("/c/organizations/add").catch(report_error)
    }
    render(React.createElement(App), document.getElementById("root")!)
    log.debug("start_web_app() - eagerly requesting new board templates ...")
    eagerly_sync_board_templates().catch(report_error)
    log.debug("start_web_app() - eagerly requesting background and title images ...")
    board_resource.read(background_images_board_uid)
    board_resource.read(title_images_board_uid)
    can_call_faas_promise()
        .then(() => set_user_currency().catch(report_error))
        .catch(report_error)
    // For testing.
    if (location.hostname.startsWith("dev-")) {
        ;(window as any).test_helper = {
            load_js,
            logout,
            goto_board,
            start_drag_card,
        }
    }
    return true
}

function _detect_and_report_login_or_signup() {
    const event = safe_session_storage.getItem("authenticate.method") as
        | "login"
        | "signup"
        | undefined
    if (!event) {
        return false
    }
    const auth_provider = parseInt(
        safe_session_storage.getItem("authenticate.provider")!,
    ) as unknown as AuthProvider
    safe_session_storage.removeItem("authenticate.method")
    safe_session_storage.removeItem("authenticate.provider")
    if (event === "signup") {
        const ttfl_start = safe_session_storage.getItem("ttfl_start")
        const ttfl = ttfl_start ? Date.now() - parseInt(ttfl_start) : 0
        analytics_initialized
            .then(() => {
                report_user_event({sign_up: new SignUp({auth_provider, ttfl})})
            })
            .catch(report_error)
    } else {
        analytics_initialized
            .then(() => {
                report_user_event({login: new Login({auth_provider})})
            })
            .catch(report_error)
    }
    safe_session_storage.removeItem("ttfl_start")
    return event === "signup"
}

function _sync_auth_providers_with_server() {
    register_auth_listener(async ({auth_providers}) => {
        if (
            JSON.stringify([...auth_providers].sort()) ===
            JSON.stringify([...state.current_user.account_auth.auth_providers].sort())
        ) {
            return
        }
        await call_function(
            new PatchFullAccountRequest({
                patch_uid: create_PatchUID(),
                patch_account_auth: new PatchAccountAuth({
                    auth_providers,
                }),
            }),
        )
    })
}

/**
 * Monitor `current_user` as well as the auth state (Google Identity Platform's id-token)
 * and enforce refreshing the id-token if there is any mismatch.
 */
function _sync_claims() {
    const gip = {
        team_uids: new Set<TeamUID>(),
        admin_organization_uids: new Set<OrganizationUID>(),
        feature_switches: new Set<FeatureSwitch>(),
        plan: Plan.free,
    }
    makeObservable(gip, {
        team_uids: observable.shallow,
        admin_organization_uids: observable.shallow,
        feature_switches: observable.shallow,
        plan: observable,
    })
    let autorun_dispose: () => any
    register_auth_listener(async ({team_uids, admin_organization_uids, access_token_claims}) => {
        runInAction(() => {
            gip.team_uids = new Set(team_uids)
            gip.admin_organization_uids = new Set(admin_organization_uids)
            gip.feature_switches = new Set(
                access_token_claims["https://cling.com/feature_switches"] || [],
            )
            gip.plan = access_token_claims["https://cling.com/plan"] || Plan.free
        })
        if (!autorun_dispose) {
            autorun_dispose = autorun(() => {
                const a = state.current_user.account_attributes
                let refresh = false
                if (!Set_equal(new Set(a.team_uids), gip.team_uids)) {
                    log.info(
                        "`team_uids` in JWT and `current_user` do not match - enforcing refresh",
                    )
                    refresh = true
                } else if (
                    !Set_equal(new Set(a.admin_organization_uids), gip.admin_organization_uids)
                ) {
                    log.info(
                        "`admin_organization_uids` in JWT and `current_user` do not match - enforcing refresh",
                    )
                    refresh = true
                } else if (!Set_equal(new Set(a.feature_switches), gip.feature_switches)) {
                    log.info(
                        "`feature_switches` in JWT and `current_user` do not match - enforcing refresh",
                    )
                    refresh = true
                } else if (a.plan !== gip.plan) {
                    log.info("`plan` in JWT and `current_user` do not match - enforcing refresh")
                    refresh = true
                }
                if (refresh) {
                    enforce_refresh()
                }
            })
        }
    })
}

/**
 * Listen for changes in in account*-caches and update `state.current_user` accordingly.
 * Store the `source_campaign` to the session storage as well.
 */
function _sync_current_user(caches: {
    [SyncEntityType.account]: LocalCache<AccountUID, Account>
    [SyncEntityType.account_settings]: LocalCache<AccountUID, AccountSettings>
    [SyncEntityType.account_attributes]: LocalCache<AccountUID, AccountAttributes>
    [SyncEntityType.account_auth]: LocalCache<AccountUID, AccountAuth>
    [SyncEntityType.account_analytics]: LocalCache<AccountUID, AccountAnalytics>
}) {
    autorun(
        () => {
            const account_uid = state.current_user.account.uid
            const account = caches[SyncEntityType.account].get(account_uid)
            const account_settings = caches[SyncEntityType.account_settings].get(account_uid)
            const account_analytics = caches[SyncEntityType.account_analytics].get(account_uid)
            const account_attributes = caches[SyncEntityType.account_attributes].get(account_uid)
            const account_auth = caches[SyncEntityType.account_auth].get(account_uid)
            runInAction(() => {
                if (account.version > state.current_user.account.version) {
                    state.current_user.account = account
                }
                if (account_settings.version > state.current_user.account_settings.version) {
                    state.current_user.account_settings = account_settings
                }
                if (account_analytics.version > state.current_user.account_analytics.version) {
                    state.current_user.account_analytics = account_analytics
                }
                if (account_attributes.version > state.current_user.account_attributes.version) {
                    state.current_user.account_attributes = account_attributes
                }
                if (account_auth.version > state.current_user.account_auth.version) {
                    state.current_user.account_auth = account_auth
                }
            })
            if (account_attributes?.source_campaign) {
                store_source_campaign_to_local_storage(account_attributes.source_campaign)
            }
        },
        {name: "startup.ts - current_user.account*"},
    )
}

function call_search_factory(search_bus: EventBus<Search | SendSearchResult>) {
    const outstanding = new Map<string, ControllablePromise<CallSearchResult>>()
    search_bus.addEventListener((event) => {
        const msg = event.data
        if (msg.type === "send_search_result") {
            const promise = outstanding.get(msg.search_id)
            if (!promise) {
                log.info("Got a search result for an unknown request - ignoring it", {
                    search_id: msg.search_id,
                })
                return
            }
            log.debug("Got search result from worker", {
                num_matching_cards: msg.matches.length,
                num_indexed_board_uid: msg.indexed_board_uids.length,
            })
            outstanding.delete(msg.search_id)
            promise.resolve({matches: msg.matches, indexed_board_uids: msg.indexed_board_uids})
        }
    })
    return async function (query: SearchQuery) {
        const search_id = random_uid("_")
        const promise = new_ControllablePromise<CallSearchResult>()
        outstanding.set(search_id, promise)
        search_bus.postMessage({type: "search", search_id, query})
        return promise
    }
}

function eagerly_request_sync(request_sync_bus: EventBus<RequestSync | RequestSyncAllFromStore>) {
    request_sync_bus.postMessage({
        type: "request_sync_all_from_store",
        sync_entity_type: SyncEntityType.board_info,
    })
    request_sync_bus.postMessage({
        type: "request_sync_all_from_store",
        sync_entity_type: SyncEntityType.account,
    })
    request_sync_bus.postMessage({
        type: "request_sync_all_from_store",
        sync_entity_type: SyncEntityType.team,
    })
}

/**
 * Load all the template boards as soon as possible.
 */
async function eagerly_sync_board_templates() {
    const template_board = await board_resource.wait_for_sync(board_templates_board_uid)
    const board_uids = template_board.regular_cards
        .map((x) =>
            x.link ? extract_linked_board_and_card_uid(x.link?.url)?.board_uid : undefined,
        )
        .filter((x) => !!x) as BoardUID[]
    for (const x of board_uids) {
        board_resource.read(x)
    }
}

/**
 * Start the worker and wait until it answers a `ping` message.
 */
async function start_worker({
    account_uid,
    ping_message,
}: {
    account_uid: string
    ping_message: Ping
}): Promise<Pick<MessagePort, "postMessage" | "addEventListener">> {
    const name = `cling:${cling.version}:${account_uid}`
    type PORT = Pick<MessagePort, "postMessage" | "addEventListener" | "removeEventListener">
    const use_shared_worker = window.SharedWorker && !location.search.includes("__force_worker")
    const start = (): PORT => {
        if (use_shared_worker) {
            log.info("Starting / connecting SharedWorker ...")
            const worker = new SharedWorker(process.env.WORKER_URI!, {name})
            worker.port.start()
            return worker.port
        } else {
            log.info("Starting Worker ...")
            return new Worker(process.env.WORKER_URI!, {name})
        }
    }
    const wait_for_connection = (port: PORT, timeout: number) =>
        new Promise<void>((resolve, reject) => {
            const timeout_var = setTimeout(() => {
                clean_up()
                reject(new TimeoutError("Can't connect to worker"))
            }, timeout)
            const clean_up = () => {
                port.removeEventListener("message", on_message)
                port.removeEventListener("messageerror", on_message_error)
                clearTimeout(timeout_var)
            }
            const on_message_error = (error: any) => {
                clean_up()
                reject(error)
            }
            const on_message = ({data: {msg}}: {data: any}) => {
                if (msg.type === "pong") {
                    clean_up()
                    resolve()
                }
            }
            port.addEventListener("message", on_message)
            port.addEventListener("messageerror", on_message_error)
            port.postMessage({msg: ping_message})
        })
    const port = start()
    await wait_for_connection(port, 20_000)
    return port
}

/**
 * Extract the board-uid to load eagerly.
 * Note that we only try to eagerly load a board if this is a "pure" board-URL meaning
 * that this is not an invite or a direct card link or anything else. System boards are
 * ignored, too.
 */
export function board_uid_hint(url: string): BoardUID | undefined {
    const board_uid = extract_linked_board_and_card_uid(url)?.board_uid
    if (!board_uid) {
        return undefined
    }
    if (is_system_board_uid(board_uid)) {
        return undefined
    }
    if (new URL(url).pathname.split("/").length !== 3) {
        return undefined
    }
    return board_uid
}
